import React from 'react';
import { ArrowLeft, Folder, HelpCircle, Bell } from 'lucide-react';
import { motion } from 'framer-motion';

const phases = [
    { id: 'plan', label: 'Plan', icon: '🧠' },
    { id: 'repurpose', label: 'Repurpose', icon: '🎨' },
    { id: 'distribute', label: 'Distribute', icon: '🚀' },
];

const StudioNavbar = ({ activePhase, onPhaseChange, onToggleVault, onNavigateBack, episode }) => {
    return (
        <header className="h-[72px] flex items-center justify-between px-6 z-20 relative">
            {/* Left: Back & Branding */}
            <div className="flex items-center gap-4">
                <button
                    onClick={onNavigateBack}
                    className="p-2.5 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-white hover:shadow-sm border border-transparent hover:border-slate-200 transition-all active:scale-95"
                >
                    <ArrowLeft size={18} strokeWidth={2.5} />
                </button>

                <div className="hidden md:block">
                    <h1 className="text-base font-bold text-slate-900 font-['Outfit'] leading-tight">
                        {episode?.episodeTitle || 'Untitled Episode'}
                    </h1>
                    <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-md">
                            Studio
                        </span>
                        <span className="text-xs text-slate-500 font-medium truncate max-w-[150px]">
                            {episode?.founderName || 'Guest'}
                        </span>
                    </div>
                </div>
            </div>

            {/* Center: Phase Switcher (The "Pill") */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="flex items-center bg-white border-2 border-slate-100 shadow-sm rounded-full p-1 gap-1">
                    {phases.map((phase) => {
                        const isActive = activePhase === phase.id;
                        return (
                            <button
                                key={phase.id}
                                onClick={() => onPhaseChange(phase.id)}
                                className={`
                                    relative px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 flex items-center gap-2
                                    ${isActive
                                        ? 'text-indigo-700'
                                        : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'}
                                `}
                            >
                                {isActive && (
                                    <motion.div
                                        layoutId="phase-pill"
                                        className="absolute inset-0 bg-indigo-50 border border-indigo-100 rounded-full shadow-sm"
                                        initial={false}
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    />
                                )}
                                <span className="relative z-10">{phase.icon}</span>
                                <span className="relative z-10 hidden sm:inline">{phase.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Right: Actions & Vault */}
            <div className="flex items-center gap-3">

                <button
                    onClick={onToggleVault}
                    className="group flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-slate-100 hover:border-indigo-200 rounded-xl text-slate-600 hover:text-indigo-600 shadow-sm hover:shadow-md transition-all active:scale-95"
                >
                    <div className="relative">
                        <Folder size={18} strokeWidth={2.5} className="group-hover:fill-indigo-100 transition-colors" />
                        {/* Notification Dot Example */}
                        {/* <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 border-2 border-white rounded-full"></span> */}
                    </div>
                    <span className="text-xs font-bold uppercase tracking-wide hidden sm:inline">Vault</span>
                </button>

                <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 overflow-hidden ml-2">
                    {/* User Avatar Placeholder */}
                    <div className="w-full h-full flex items-center justify-center text-slate-400">
                        <span className="text-xs font-bold">You</span>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default StudioNavbar;
